import React, { useEffect, useState } from "react"
import axios from "axios"
import { useSelector, connect } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  Label,
  Form,
  FormGroup,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import config from "config/config"
import SnackbarComponent from "components/SnackBar"
import PrimaryButton from "newcomponents/primary-button"
import { setBreadcrumbItems } from "../../store/actions"

// Small uppercase micro-label to match the field-label style used across
// the app's newer forms (see CreateAudience's modal) instead of reactstrap's
// plain bold Label.
const SectionLabel = ({ children, isDark, className = "" }) => (
  <Label
    className={className}
    style={{
      fontSize: 12,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: isDark ? "#9ca3af" : "#6b7280",
      marginBottom: 6,
    }}
  >
    {children}
  </Label>
)

// A checkbox + title + description rendered as its own bordered row instead
// of a bare inline checkbox - groups related on/off settings so they read as
// distinct options rather than blending into the surrounding text.
const ToggleOption = ({ checked, onChange, title, description, isDark }) => (
  <label
    style={{
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
      padding: "12px 14px",
      borderRadius: 10,
      border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
      backgroundColor: checked
        ? isDark
          ? "#1e3a5f"
          : "#EBF5FF"
        : isDark
          ? "#1f2937"
          : "#f9fafb",
      cursor: "pointer",
      marginBottom: 0,
    }}
  >
    <input
      type="checkbox"
      className="app-checkbox"
      checked={checked}
      onChange={onChange}
      style={{ marginTop: 2, flexShrink: 0 }}
    />
    <span>
      <span style={{ fontWeight: 600, fontSize: 14, display: "block" }}>
        {title}
      </span>
      {description && (
        <span
          className="text-muted"
          style={{ fontSize: 12.5, display: "block", marginTop: 2 }}
        >
          {description}
        </span>
      )}
    </span>
  </label>
)

const sectionHeaderStyle = isDark => ({
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  backgroundColor: isDark ? "#252F40" : "#fff",
  borderBottom: `1px solid ${isDark ? "#374151" : "#f0f0f0"}`,
})

// Shared look for every text/select/date/file field on this page - soft
// filled background instead of Bootstrap's plain white/grey-border default,
// with a visible focus ring via the "app-field" class (:focus needs real
// CSS, not an inline style) since setting `border` inline would otherwise
// suppress Bootstrap's own focus border-color change.
const inputStyle = isDark => ({
  borderRadius: 8,
  border: `1px solid ${isDark ? "#374151" : "#e2e5ea"}`,
  backgroundColor: isDark ? "#1f2937" : "#f9fafb",
  color: isDark ? "#fff" : "#111827",
  padding: "10px 14px",
  fontSize: 14,
})

const CreateSocialCampaign = props => {
  document.title = "Create Social Media Campaign | AeraVue"

  const navigate = useNavigate()
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)
  const mode = useSelector(state => state.Layout.layoutMode)
  const isDark = mode === "dark"

  const breadcrumbItems = [
    { title: `${activeOrganization.name}`, link: "#" },
    { title: "Social Media Campaigns", link: "/dashboard/social-campaigns" },
    { title: "Create", link: "#" },
  ]

  useEffect(() => {
    props.setBreadcrumbItems("Create Social Media Campaign", breadcrumbItems)
  }, [])

  const [form, setForm] = useState({
    campaignTitle: "",
    campaignDescription: "",
    startDate: "",
    endDate: "",
    frequency: 2,
    cycle: "daily",
    platforms: [],
    topic: "",
    imageStyle: "",
    logoUrl: activeOrganization.logo || "",
    hashtags: "",
    includeOrgNameInImage: false,
    includeTestimonials: false,
    includeTestimonialsInImage: false,
    manualTestimonials: [],
  })
  const [plan, setPlan] = useState(null)
  const [generatingPlan, setGeneratingPlan] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [err, setErr] = useState(null)
  const [success, setSuccess] = useState(null)
  const [referenceModalIndex, setReferenceModalIndex] = useState(null)
  const [uploadingReferenceImage, setUploadingReferenceImage] = useState(false)

  const handleLogoUpload = async e => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploadingLogo(true)
    setErr(null)
    try {
      const formData = new FormData()
      formData.append("file", file)
      const response = await axios.post(
        `${config.backendUrl}/aws/file?email=reputation-management`,
        formData,
      )
      setForm(prev => ({ ...prev, logoUrl: response.data.url }))
    } catch (error) {
      setErr("Failed to upload logo. Please try again.")
    } finally {
      setUploadingLogo(false)
    }
  }

  const handleReferenceImageUpload = async (dayIndex, e) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploadingReferenceImage(true)
    setErr(null)
    try {
      const formData = new FormData()
      formData.append("file", file)
      const response = await axios.post(
        `${config.backendUrl}/aws/file?email=reputation-management`,
        formData,
      )
      updatePlanDay(dayIndex, "referenceImageUrl", response.data.url)
    } catch (error) {
      setErr("Failed to upload reference image. Please try again.")
    } finally {
      setUploadingReferenceImage(false)
    }
  }

  const toggleSelectedPlatform = platform => {
    setForm(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform],
    }))
  }

  const updateManualTestimonial = (index, field, value) => {
    setForm(prev => {
      const manualTestimonials = [...prev.manualTestimonials]
      manualTestimonials[index] = { ...manualTestimonials[index], [field]: value }
      return { ...prev, manualTestimonials }
    })
  }

  const addManualTestimonial = () => {
    setForm(prev => ({
      ...prev,
      manualTestimonials: [...prev.manualTestimonials, { name: "", text: "" }],
    }))
  }

  const removeManualTestimonial = index => {
    setForm(prev => ({
      ...prev,
      manualTestimonials: prev.manualTestimonials.filter((_, i) => i !== index),
    }))
  }

  const getValidManualTestimonials = () =>
    form.includeTestimonials
      ? form.manualTestimonials.filter(t => t.name.trim() && t.text.trim())
      : []

  const handleGeneratePlan = async () => {
    setErr(null)
    if (!form.startDate || !form.endDate || form.platforms.length === 0) {
      setErr(
        "Pick a date range and at least one platform before generating AI suggestions.",
      )
      return
    }

    setGeneratingPlan(true)
    try {
      const response = await axios.post(
        `${config.backendUrl}/social-media-campaigns/generate-plan`,
        {
          organizationId: activeOrganization.id,
          startDate: form.startDate,
          endDate: form.endDate,
          platforms: form.platforms,
          topic: form.topic,
          frequency: Number(form.frequency),
          cycle: form.cycle,
          includeTestimonials: form.includeTestimonials,
          manualTestimonials: getValidManualTestimonials(),
        },
        { headers: { Authorization: `Bearer ${userData.token}` } },
      )
      setPlan(response.data.plan)
    } catch (error) {
      setErr(
        error.response?.data?.message || "Failed to generate AI campaign plan.",
      )
    } finally {
      setGeneratingPlan(false)
    }
  }

  const updatePlanField = (field, value) => {
    setPlan(prev => ({ ...prev, [field]: value }))
  }

  const updatePlanDay = (index, field, value) => {
    setPlan(prev => {
      const days = [...(prev.days || [])]
      days[index] = { ...days[index], [field]: value }
      return { ...prev, days }
    })
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setErr(null)

    if (!form.campaignTitle.trim()) {
      setErr("Campaign title is required.")
      return
    }
    if (!form.startDate || !form.endDate) {
      setErr("Start and end dates are required.")
      return
    }
    if (form.endDate < form.startDate) {
      setErr("End date cannot be before the start date.")
      return
    }
    if (form.platforms.length === 0) {
      setErr("Select at least one platform.")
      return
    }

    setSubmitting(true)
    try {
      const response = await axios.post(
        `${config.backendUrl}/social-media-campaigns`,
        {
          organizationId: activeOrganization.id,
          campaignTitle: form.campaignTitle,
          campaignDescription: form.campaignDescription,
          startDate: form.startDate,
          endDate: form.endDate,
          frequency: Number(form.frequency),
          cycle: form.cycle,
          platforms: form.platforms,
          aiTheme: plan?.theme,
          aiTone: plan?.tone,
          imageStyle: form.imageStyle,
          logoUrl: form.logoUrl,
          hashtags: form.hashtags,
          includeOrgNameInImage: form.includeOrgNameInImage,
          includeTestimonialsInImage: form.includeTestimonials && form.includeTestimonialsInImage,
          aiSettings: plan,
        },
        { headers: { Authorization: `Bearer ${userData.token}` } },
      )

      navigate(`/dashboard/social-campaigns/${response.data.campaign.id}`)
    } catch (error) {
      setErr(
        error.response?.data?.message ||
          "Failed to create social media campaign.",
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <React.Fragment>
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />

      <Form onSubmit={handleSubmit}>
        <Card
          className="mb-4 shadow-sm br"
          style={{ backgroundColor: isDark ? "#252F40" : "#fff" }}
        >
          <CardHeader style={sectionHeaderStyle(isDark)}>
            <h5 className="mb-0">Campaign Details</h5>
            <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
              What this campaign is called and what it's about.
            </p>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <SectionLabel isDark={isDark}>Campaign Title</SectionLabel>
                  <Input
                    className="app-field"
                    style={inputStyle(isDark)}
                    value={form.campaignTitle}
                    onChange={e =>
                      setForm({ ...form, campaignTitle: e.target.value })
                    }
                    placeholder="e.g. Fall Client Testimonials"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <SectionLabel isDark={isDark}>
                    Seed Topic / Goal (optional)
                  </SectionLabel>
                  <Input
                    className="app-field"
                    style={inputStyle(isDark)}
                    value={form.topic}
                    onChange={e => setForm({ ...form, topic: e.target.value })}
                    placeholder="e.g. Promote our new dermatology services"
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup className="mb-0">
              <SectionLabel isDark={isDark}>Campaign Description</SectionLabel>
              <Input
                type="textarea"
                rows={5}
                className="app-field"
                style={inputStyle(isDark)}
                value={form.campaignDescription}
                onChange={e =>
                  setForm({ ...form, campaignDescription: e.target.value })
                }
                placeholder="What should this campaign promote? The AI plan and every post's caption are grounded in this text."
              />
            </FormGroup>
          </CardBody>
        </Card>

        <Card
          className="mb-4 shadow-sm br"
          style={{ backgroundColor: isDark ? "#252F40" : "#fff" }}
        >
          <CardHeader style={sectionHeaderStyle(isDark)}>
            <h5 className="mb-0">Branding &amp; Visual Style</h5>
            <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
              Controls how generated images and hashtags look.
            </p>
          </CardHeader>
          <CardBody>
            <FormGroup>
              <SectionLabel isDark={isDark}>
                Imagery Style (optional)
              </SectionLabel>
              <Input
                className="app-field"
                style={inputStyle(isDark)}
                value={form.imageStyle}
                onChange={e =>
                  setForm({ ...form, imageStyle: e.target.value })
                }
                placeholder="e.g. abstract tech graphics, match our brand colors, no people, our own team photos"
              />
              <small className="text-muted">
                By default, generated images are real photographs (not
                illustrations) and avoid photos of people. Tell the AI what
                to do instead if you'd like a specific look - note that
                illustration/graphic styles are more likely to have stray
                text baked in by the AI than photographs are, so review
                before approving if you request one.
              </small>
            </FormGroup>
            <FormGroup>
              <SectionLabel isDark={isDark}>Brand Logo</SectionLabel>
              <div className="d-flex align-items-center gap-3 mb-3">
                {form.logoUrl ? (
                  <img
                    src={form.logoUrl}
                    alt="Brand logo"
                    style={{
                      width: 64,
                      height: 64,
                      objectFit: "contain",
                      border: "1px solid #e0e0e0",
                      borderRadius: 8,
                      padding: 4,
                    }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center text-muted"
                    style={{
                      width: 64,
                      height: 64,
                      border: "1px dashed #ccc",
                      borderRadius: 8,
                      fontSize: 11,
                    }}
                  >
                    No logo
                  </div>
                )}
                <div>
                  <Input
                    type="file"
                    className="app-field"
                    style={inputStyle(isDark)}
                    accept="image/*"
                    disabled={uploadingLogo}
                    onChange={handleLogoUpload}
                  />
                  <small className="text-muted">
                    Defaults to your organization's logo. Upload a different
                    image to use it for this campaign instead - it's placed
                    directly onto every generated image (not redrawn by AI),
                    so it always looks exactly right.
                  </small>
                </div>
              </div>
              <ToggleOption
                isDark={isDark}
                checked={form.includeOrgNameInImage}
                onChange={() =>
                  setForm(prev => ({
                    ...prev,
                    includeOrgNameInImage: !prev.includeOrgNameInImage,
                  }))
                }
                title="Include business name as text in the image"
                description="Leave unchecked if your logo already shows your name. Enable this only if your logo is icon-only and needs the name spelled out."
              />
            </FormGroup>
            <FormGroup className="mb-0">
              <SectionLabel isDark={isDark}>Hashtags (optional)</SectionLabel>
              <Input
                value={form.hashtags}
                className="app-field"
                style={inputStyle(isDark)}
                onChange={e => setForm({ ...form, hashtags: e.target.value })}
                placeholder="e.g. #dermatology #skincare #clientcare"
              />
              <small className="text-muted">
                Leave blank to have AI suggest 3-5 relevant hashtags per post
                automatically. If you enter your own, they're used exactly as
                typed on every post.
              </small>
            </FormGroup>
          </CardBody>
        </Card>

        <Card
          className="mb-4 shadow-sm br"
          style={{ backgroundColor: isDark ? "#252F40" : "#fff" }}
        >
          <CardHeader style={sectionHeaderStyle(isDark)}>
            <h5 className="mb-0">Testimonials</h5>
            <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
              Optionally feature real customer reviews in the schedule.
            </p>
          </CardHeader>
          <CardBody>
            <ToggleOption
              isDark={isDark}
              checked={form.includeTestimonials}
              onChange={() =>
                setForm(prev => ({
                  ...prev,
                  includeTestimonials: !prev.includeTestimonials,
                }))
              }
              title="Include testimonials"
              description="Automatically features your organization's best recent reviews (4-5 stars, from Yelp/Google/Healthgrade/Vitals) as a testimonial post - real reviews, not typed in or AI-invented. At least one scheduled day will be dedicated to it (up to 2 reviews per day) if any qualify."
            />

            {form.includeTestimonials && (
              <div className="mt-3">
                <ToggleOption
                  isDark={isDark}
                  checked={form.includeTestimonialsInImage}
                  onChange={() =>
                    setForm(prev => ({
                      ...prev,
                      includeTestimonialsInImage: !prev.includeTestimonialsInImage,
                    }))
                  }
                  title="Include testimonial text in the image"
                  description="The AI will attempt to render the quote directly onto the testimonial post's image (styled like a quote card). AI-rendered text can occasionally come out garbled - review before approving. Leave unchecked to keep the testimonial in the caption only."
                />

                <div
                  className="mt-3 pt-3"
                  style={{
                    borderTop: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                  }}
                >
                  <SectionLabel isDark={isDark} className="mb-1">
                    Or add a testimonial manually (optional)
                  </SectionLabel>
                  <small className="text-muted d-block mb-2">
                    For a real testimonial from somewhere we don't
                    automatically pull reviews from (e.g. a note emailed in
                    directly). Manually entered testimonials are always used
                    first; auto-fetched reviews only fill any remaining
                    slots.
                  </small>

                  {form.manualTestimonials.map((t, i) => (
                    <Row key={i} className="mb-2 align-items-start">
                      <Col md={3}>
                        <Input
                          className="app-field"
                          style={inputStyle(isDark)}
                          value={t.name}
                          onChange={e =>
                            updateManualTestimonial(i, "name", e.target.value)
                          }
                          placeholder="Client name"
                        />
                      </Col>
                      <Col md={8}>
                        <Input
                          type="textarea"
                          rows={2}
                          className="app-field"
                          style={inputStyle(isDark)}
                          value={t.text}
                          onChange={e =>
                            updateManualTestimonial(i, "text", e.target.value)
                          }
                          placeholder="Testimonial text"
                        />
                      </Col>
                      <Col md={1}>
                        <Button
                          type="button"
                          color="danger"
                          outline
                          size="sm"
                          onClick={() => removeManualTestimonial(i)}
                        >
                          &times;
                        </Button>
                      </Col>
                    </Row>
                  ))}

                  <Button
                    type="button"
                    color="secondary"
                    outline
                    size="sm"
                    onClick={addManualTestimonial}
                  >
                    + Add a testimonial
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        <Card
          className="mb-4 shadow-sm br"
          style={{ backgroundColor: isDark ? "#252F40" : "#fff" }}
        >
          <CardHeader style={sectionHeaderStyle(isDark)}>
            <h5 className="mb-0">Schedule &amp; Platforms</h5>
            <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
              When posts go out and where.
            </p>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md={3}>
                <FormGroup>
                  <SectionLabel isDark={isDark}>Start Date</SectionLabel>
                  <Input
                    type="date"
                    className="app-field"
                    style={inputStyle(isDark)}
                    value={form.startDate}
                    onChange={e =>
                      setForm({ ...form, startDate: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <SectionLabel isDark={isDark}>End Date</SectionLabel>
                  <Input
                    type="date"
                    className="app-field"
                    style={inputStyle(isDark)}
                    value={form.endDate}
                    onChange={e =>
                      setForm({ ...form, endDate: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <SectionLabel isDark={isDark}>Cycle</SectionLabel>
                  <Input
                    type="select"
                    className="app-field"
                    style={inputStyle(isDark)}
                    value={form.cycle}
                    onChange={e => setForm({ ...form, cycle: e.target.value })}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <SectionLabel isDark={isDark}>
                    Post every N{" "}
                    {form.cycle === "weekly" ? "week(s)" : "day(s)"}
                  </SectionLabel>
                  <Input
                    type="number"
                    min={1}
                    className="app-field"
                    style={inputStyle(isDark)}
                    value={form.frequency}
                    onChange={e =>
                      setForm({ ...form, frequency: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup className="mb-0">
              <SectionLabel isDark={isDark}>Platforms</SectionLabel>
              <div className="d-flex flex-wrap gap-3">
                {[
                  { key: "facebook", label: "Facebook", icon: "mdi-facebook" },
                  { key: "instagram", label: "Instagram", icon: "mdi-instagram" },
                ].map(({ key, label, icon }) => {
                  const selected = form.platforms.includes(key)
                  return (
                    <label
                      key={key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        padding: "10px 16px",
                        borderRadius: 10,
                        border: `1px solid ${
                          selected ? "#2990DF" : isDark ? "#374151" : "#e5e7eb"
                        }`,
                        backgroundColor: selected
                          ? isDark
                            ? "#1e3a5f"
                            : "#EBF5FF"
                          : isDark
                            ? "#1f2937"
                            : "#f9fafb",
                        fontWeight: 500,
                        fontSize: 14,
                      }}
                    >
                      <input
                        type="checkbox"
                        className="app-checkbox"
                        checked={selected}
                        onChange={() => toggleSelectedPlatform(key)}
                      />
                      <i className={`mdi ${icon}`} style={{ fontSize: 16 }} />
                      {label}
                    </label>
                  )
                })}
              </div>
            </FormGroup>
          </CardBody>
        </Card>

        <Card
          className="mb-4 shadow-sm br"
          style={{ backgroundColor: isDark ? "#252F40" : "#fff" }}
        >
          <CardHeader style={sectionHeaderStyle(isDark)}>
            <h5 className="mb-0">AI Content Plan</h5>
            <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
              Preview and edit the theme, tone, and per-day topics before
              creating.
            </p>
          </CardHeader>
          <CardBody>
            {!plan && (
              <div className="text-center py-4">
                <p
                  className="text-muted mx-auto mb-4"
                  style={{ maxWidth: 480 }}
                >
                  Pick a date range and platform(s) above, then generate AI
                  suggestions for the campaign's theme, tone, and post
                  topics. You can review and edit everything before
                  confirming.
                </p>
                <PrimaryButton
                  type="button"
                  disabled={generatingPlan}
                  onClick={handleGeneratePlan}
                  style={{ padding: "12px 36px", borderRadius: 8 }}
                >
                  {generatingPlan ? (
                    <Spinner size="sm" />
                  ) : (
                    "Get AI Suggestions"
                  )}
                </PrimaryButton>
              </div>
            )}

            {plan && (
              <>
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    type="button"
                    color="secondary"
                    outline
                    size="sm"
                    disabled={generatingPlan}
                    onClick={handleGeneratePlan}
                  >
                    {generatingPlan ? (
                      <Spinner size="sm" />
                    ) : (
                      "Regenerate Suggestions"
                    )}
                  </Button>
                </div>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <SectionLabel isDark={isDark}>Theme</SectionLabel>
                      <Input
                        className="app-field"
                        style={inputStyle(isDark)}
                        value={plan.theme || ""}
                        onChange={e => updatePlanField("theme", e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <SectionLabel isDark={isDark}>Tone</SectionLabel>
                      <Input
                        className="app-field"
                        style={inputStyle(isDark)}
                        value={plan.tone || ""}
                        onChange={e => updatePlanField("tone", e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <SectionLabel isDark={isDark}>
                  Day-by-Day Plan ({(plan.days || []).length} scheduled post
                  {(plan.days || []).length === 1 ? "" : "s"})
                </SectionLabel>
                <small className="text-muted d-block mb-2">
                  Edit any day's topic or imagery below - these seed that
                  day's AI-generated caption and image once the campaign is
                  created. Each post's full prompts stay editable afterward
                  too, with a per-post Regenerate.
                </small>
                {plan.testimonialNote && (
                  <p className="text-warning mb-2" style={{ fontSize: 13 }}>
                    {plan.testimonialNote}
                  </p>
                )}
                <div
                  style={{
                    overflowX: "auto",
                    border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                    borderRadius: 10,
                  }}
                >
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 100 }}>Date</th>
                        <th style={{ minWidth: 220 }}>Topic / Angle</th>
                        <th style={{ minWidth: 220 }}>Image Style</th>
                        <th style={{ minWidth: 200 }}>Reference (optional)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(plan.days || []).map((d, i) => (
                        <tr key={i}>
                          <td className="text-nowrap">
                            {d.date ? new Date(d.date).toLocaleDateString() : ""}
                          </td>
                          <td>
                            {d.testimonials?.length > 0 && (
                              <span
                                className="badge bg-info text-dark d-block mb-1"
                                style={{ fontWeight: "normal" }}
                              >
                                Testimonial:{" "}
                                {d.testimonials.map(t => t.name).join(" & ")}
                              </span>
                            )}
                            <Input
                              className="app-field"
                              style={inputStyle(isDark)}
                              value={d.topic || ""}
                              onChange={e =>
                                updatePlanDay(i, "topic", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <Input
                              className="app-field"
                              style={inputStyle(isDark)}
                              value={d.imageStyle || ""}
                              onChange={e =>
                                updatePlanDay(i, "imageStyle", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <Button
                              type="button"
                              color="link"
                              size="sm"
                              className="p-0"
                              onClick={() => setReferenceModalIndex(i)}
                            >
                              {d.referenceUrl || d.referenceImageUrl || d.notes
                                ? "Edit reference"
                                : "+ Add reference"}
                            </Button>
                            {d.referenceImageUrl && (
                              <small className="text-muted d-block">
                                Image uploaded
                              </small>
                            )}
                            {!d.referenceImageUrl && d.referenceUrl && (
                              <small className="text-muted d-block">
                                URL set
                              </small>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>

        <Modal
          isOpen={referenceModalIndex !== null}
          toggle={() => setReferenceModalIndex(null)}
        >
          <ModalHeader toggle={() => setReferenceModalIndex(null)}>
            {referenceModalIndex !== null && plan?.days?.[referenceModalIndex]?.date
              ? `Reference for ${new Date(
                  plan.days[referenceModalIndex].date,
                ).toLocaleDateString()}`
              : "Reference"}
          </ModalHeader>
          {referenceModalIndex !== null && plan?.days?.[referenceModalIndex] && (
            <ModalBody>
              <FormGroup>
                <Label className="mb-1">Upload an image (optional)</Label>
                <Input
                  type="file"
                  className="app-field"
                  style={inputStyle(isDark)}
                  accept="image/*"
                  disabled={uploadingReferenceImage}
                  onChange={e =>
                    handleReferenceImageUpload(referenceModalIndex, e)
                  }
                />
                {uploadingReferenceImage && (
                  <Spinner size="sm" className="mt-2" />
                )}
                {plan.days[referenceModalIndex].referenceImageUrl && (
                  <div className="d-flex align-items-center gap-2 mt-2">
                    <img
                      src={plan.days[referenceModalIndex].referenceImageUrl}
                      alt="Reference"
                      style={{
                        width: 64,
                        height: 64,
                        objectFit: "cover",
                        borderRadius: 6,
                        border: "1px solid #e0e0e0",
                      }}
                    />
                    <Button
                      type="button"
                      color="danger"
                      outline
                      size="sm"
                      onClick={() =>
                        updatePlanDay(referenceModalIndex, "referenceImageUrl", "")
                      }
                    >
                      Remove
                    </Button>
                  </div>
                )}
                <small className="text-muted d-block mt-1">
                  This exact image gets placed on the generated post image.
                  Takes priority over the reference URL below if both are set.
                </small>
              </FormGroup>

              <FormGroup>
                <Label className="mb-1">Reference URL (optional)</Label>
                <Input
                  className="app-field"
                  style={inputStyle(isDark)}
                  value={plan.days[referenceModalIndex].referenceUrl || ""}
                  onChange={e =>
                    updatePlanDay(referenceModalIndex, "referenceUrl", e.target.value)
                  }
                  placeholder="https://example.com"
                />
                <small className="text-muted d-block mt-1">
                  A real screenshot of this URL will be captured and placed
                  on the image (unless an uploaded image is set above).
                </small>
              </FormGroup>

              <FormGroup>
                <Label className="mb-1">Label/name (optional)</Label>
                <Input
                  className="app-field"
                  style={inputStyle(isDark)}
                  value={plan.days[referenceModalIndex].referenceLabel || ""}
                  onChange={e =>
                    updatePlanDay(referenceModalIndex, "referenceLabel", e.target.value)
                  }
                  placeholder="e.g. the client or project name"
                />
              </FormGroup>

              <FormGroup className="mb-0">
                <Label className="mb-1">Extra notes/context for the AI (optional)</Label>
                <Input
                  type="textarea"
                  rows={3}
                  className="app-field"
                  style={inputStyle(isDark)}
                  value={plan.days[referenceModalIndex].notes || ""}
                  onChange={e =>
                    updatePlanDay(referenceModalIndex, "notes", e.target.value)
                  }
                  placeholder="Anything else the AI should factor into this day's caption and image"
                />
              </FormGroup>
            </ModalBody>
          )}
          <ModalFooter>
            <Button
              type="button"
              color="secondary"
              onClick={() => setReferenceModalIndex(null)}
            >
              Done
            </Button>
          </ModalFooter>
        </Modal>

        <Row className="justify-content-center mb-4">
          <Col
            xl={12}
            className="d-flex justify-content-center align-items-center gap-2"
          >
            <PrimaryButton
              style={{ padding: "10px 30px", borderRadius: "8px" }}
              type="submit"
              disabled={submitting}
            >
              {submitting ? <Spinner size="sm" /> : "Create Campaign"}
            </PrimaryButton>
            <Button
              color="danger"
              type="button"
              style={{ padding: "10px 30px", borderRadius: "8px" }}
              onClick={() => navigate("/dashboard/social-campaigns")}
            >
              Cancel
            </Button>
          </Col>
        </Row>
      </Form>
    </React.Fragment>
  )
}

export default connect(null, { setBreadcrumbItems })(CreateSocialCampaign)
